import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { File, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DocumentsActionCell } from "@/components/documents/tables/documents-actions";
import { SortableColumn } from "@/components/shared/sortable-column";
import { IDCell } from "@/components/shared/id-cell";
import React from "react";
import { format } from "date-fns";
import { toast } from "sonner";

export function getDocumentsColumns({
    setActiveSection,
    formatFileSize,
    getStatusColor,
    truncateFileName,
    openDialog,
    handleDialogOpen,
    sortBy,
    sortOrder,
    handleSort,
}: {
    setActiveSection?: React.Dispatch<React.SetStateAction<string>>,
    formatFileSize: (sizeString: string) => string,
    getStatusColor: (status: string) => string,
    truncateFileName: (name: string, maxLength?: number) => string,
    openDialog: { [key: string]: boolean },
    handleDialogOpen: (documentId: string, isOpen: boolean) => void,
    sortBy: string,
    sortOrder: string,
    handleSort: (column: string) => void,
}): ColumnDef<DocumentFile>[] {
    return [
        {
            accessorKey: "name",
            header: () => (
                <SortableColumn label="Document" columnKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
            ),
            cell: ({ row }) => (
                <div className="flex items-start pl-4">
                    <File className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                        <button
                            className="hover:text-primary transition-colors duration-200 font-medium truncate"
                            onClick={() => {
                                const url = row.original.url;
                                if (url) {
                                    window.open(url, "_blank");
                                }
                            }}
                            title={row.getValue("name")}
                        >
                            {truncateFileName(row.getValue("name"))}
                        </button>
                        <IDCell id={row.original.id} className="-ml-2" />
                    </div>
                </div>
            ),
        },

        {
            accessorKey: "tags",
            header: () => <div className="text-left">Labels</div>,
            cell: ({ row }) => {
                const tags = row.original.tags || [];
                return (
                    <div className="flex flex-wrap gap-1">
                        {tags.length > 0 ? (
                            <>
                                {tags.slice(0, 2).map((tag, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                        {tag}
                                    </Badge>
                                ))}
                                {tags.length > 2 && (
                                    <Badge
                                        variant="secondary"
                                        className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                                        onClick={() => handleDialogOpen(row.original.id, true)}
                                    >
                                        +{tags.length - 2}
                                    </Badge>
                                )}
                            </>
                        ) : (
                            <span className="text-xs text-muted-foreground">No labels</span>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: "type",
            header: () => <div className="text-left">Type</div>,
            cell: ({ row }) => (
                <Badge variant="outline" className="font-mono">
                    {row.getValue("type")}
                </Badge>
            ),
        },
        {
            accessorKey: "size",
            header: () => <div className="text-left">Size</div>,
            cell: ({ row }) => (
                <div className="text-sm text-muted-foreground">
                    {formatFileSize(row.getValue("size"))}
                </div>
            ),
        },
        {
            accessorKey: "tracking_id",
            header: () => <div className="text-left">Tracking ID</div>,
            cell: ({ row }) => {
                const copyToClipboard = (text: string) => {
                    navigator.clipboard.writeText(text);
                    toast.success("Copied to clipboard");
                };
                const trackingId = row.getValue("tracking_id") as string;
                return (
                    <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
                            {trackingId.substring(0, 8)}...
                        </code>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(trackingId)}
                            title="Copy full tracking ID"
                        >
                            <Copy className="h-3 w-3" />
                        </Button>
                    </div>
                );
            },
        },
        {
            accessorKey: "status",
            header: () => <div className="text-left">Status</div>,
            cell: ({ row }) => (
                <Badge className={getStatusColor(row.getValue("status"))}>
                    {row.getValue("status")}
                </Badge>
            ),
        },
        {
            accessorKey: "created_at",
            header: () => (
                <SortableColumn
                    label="Upload Date"
                    columnKey="created_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const date = new Date(row.getValue("created_at"));
                return (
                    <div className="flex flex-col">
                        <span className="text-sm">{format(date, "MMM d, yyyy")}</span>
                        <span className="text-xs text-muted-foreground">
                            {format(date, "hh:mm a")}
                        </span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => <DocumentsActionCell row={row} setActiveSection={setActiveSection} />,
        },
    ];
}
